import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ConfirmModalProps = {
    isOpen: boolean;
    title: string;
    children?: React.ReactNode;
    details?:
        | React.ReactNode
        | ((ctx: {
              itemLabel?: string;
              count?: number;
              data?: unknown;
          }) => React.ReactNode);
    confirmText?: string;
    cancelText?: string;
    isBusy?: boolean;
    manageBusyWithPromise?: boolean;
    onConfirm: () => void | Promise<void>;
    onCancel: () => void;
    onClose?: () => void;
    disableOutsideClickWhenBusy?: boolean;
    autoFocusButton?: 'confirm' | 'cancel';
    icon?: React.ReactNode;
    ariaLabelledById?: string;
    ariaDescribedById?: string;
    variant?: 'neutral' | 'danger';
    className?: string;
    // Context for renderer functions
    itemLabel?: string;
    itemTitle?: string;
    count?: number;
    data?: unknown;
};

export function ConfirmModal(props: ConfirmModalProps) {
    const {
        isOpen,
        title,
        children,
        details,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        isBusy: isBusyProp = false,
        manageBusyWithPromise = true,
        onConfirm,
        onCancel,
        onClose,
        disableOutsideClickWhenBusy = true,
        autoFocusButton = 'cancel',
        icon,
        ariaLabelledById,
        ariaDescribedById,
        variant = 'neutral',
        className,
        itemLabel,
        itemTitle,
        count,
        data,
    } = props;

    const [internalBusy, setInternalBusy] = useState(false);
    const isBusy = isBusyProp || internalBusy;

    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const lastActiveElementRef = useRef<Element | null>(null);

    const describedById = useMemo(
        () => ariaDescribedById || 'confirm-modal-description',
        [ariaDescribedById],
    );
    const labelledById = useMemo(
        () => ariaLabelledById || 'confirm-modal-title',
        [ariaLabelledById],
    );

    const handleConfirm = useCallback(async () => {
        if (isBusy) return;
        try {
            if (manageBusyWithPromise) setInternalBusy(true);
            const result = onConfirm();
            if (
                result &&
                typeof (result as Promise<void>).then === 'function'
            ) {
                await (result as Promise<void>);
            }
        } finally {
            if (manageBusyWithPromise) setInternalBusy(false);
        }
    }, [isBusy, manageBusyWithPromise, onConfirm]);

    useEffect(() => {
        if (!isOpen) return undefined;
        lastActiveElementRef.current = document.activeElement;
        const toFocus =
            autoFocusButton === 'confirm'
                ? confirmButtonRef.current
                : cancelButtonRef.current;
        toFocus?.focus();
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isBusy) {
                (onClose || onCancel)();
            }
            if (e.key === 'Enter' && !isBusy) {
                void handleConfirm();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isBusy, autoFocusButton, onClose, onCancel, handleConfirm]);

    if (!isOpen) return null;

    const getConfirmButtonClasses = () => {
        if (variant === 'danger') {
            return isBusy
                ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600 text-neutral-100';
        }
        return isBusy
            ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
            : 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100';
    };
    const confirmButtonClasses = getConfirmButtonClasses();

    const cancelButtonClasses = isBusy
        ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
        : 'bg-neutral-500 hover:bg-neutral-400 text-neutral-100';

    const renderDetails = () => {
        if (children) return children;
        if (typeof details === 'function')
            return details({ itemLabel, count, data });
        return details || null;
    };

    return (
        <div
            className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 flex justify-center items-center z-[1100]"
            onClick={() => {
                if (isBusy && disableOutsideClickWhenBusy) return;
                (onClose || onCancel)();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Escape' && !isBusy) (onClose || onCancel)();
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledById}
            aria-describedby={describedById}
        >
            <div
                role="presentation"
                className={`bg-neutral-700 rounded-medium p-lg max-w-md w-full mx-4 shadow-large border-2 border-neutral-400 ${className || ''}`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <h3
                    id={labelledById}
                    className="text-lg font-semibold text-neutral-100 mb-4"
                >
                    {title}
                </h3>
                {itemTitle && (!count || count === 1) && (
                    <div className="bg-neutral-600 border border-neutral-500 rounded-small p-3 mb-4">
                        <p className="text-neutral-100 font-medium text-sm break-words">
                            &quot;
                            {itemTitle.length > 120
                                ? `${itemTitle.slice(0, 120)}...`
                                : itemTitle}
                            &quot;
                        </p>
                    </div>
                )}
                <div
                    id={describedById}
                    className="text-neutral-200 text-sm mb-6"
                >
                    {renderDetails()}
                </div>
                {isBusy && (
                    <div className="bg-neutral-600 border border-neutral-500 rounded-small p-3 mb-4 flex items-center gap-3">
                        {icon || (
                            <i className="fas fa-spinner fa-spin text-red-500" />
                        )}
                        <div className="text-neutral-200 text-sm">
                            <p className="font-medium">Working...</p>
                            <p className="text-xs text-neutral-300 mt-1">
                                Please wait, this may take a moment.
                            </p>
                        </div>
                    </div>
                )}
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${cancelButtonClasses}`}
                        onClick={onCancel}
                        disabled={isBusy}
                        ref={cancelButtonRef}
                        aria-label="Cancel"
                        title="Cancel"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 rounded-small font-medium transition-colors duration-200 ${confirmButtonClasses}`}
                        onClick={handleConfirm}
                        disabled={isBusy}
                        ref={confirmButtonRef}
                        aria-label={confirmText}
                        title={confirmText}
                    >
                        {isBusy ? (
                            <>
                                <i className="fas fa-spinner fa-spin mr-2" />
                                {confirmText}
                            </>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}


